// src/components/AgentModal.js
import React, { useState, useEffect } from 'react';

export default function AgentModal({ open, agent, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', domain: '', token: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (agent) {
      setForm({
        name: agent.name || '',
        domain: agent.domain || '',
        token: agent.token || '',
        note: agent.note || '',
      });
    } else {
      setForm({ name: '', domain: '', token: '', note: '' });
    }
    setError('');
  }, [agent, open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.domain.trim()) {
      setError('Name and Domain are required.');
      return;
    }
    try { new URL(form.domain.trim()); } catch {
      setError('Invalid domain URL. Example: https://mybot.example.com');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop — শুধু এখানে onClick */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      />

      <div
        className="relative w-full max-w-md card-glass rounded-2xl p-6 animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-700 text-white text-lg">
            {agent ? 'Edit Agent' : 'Add New Agent'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-white transition-colors">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 px-3 py-2.5 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field
            label="Agent Name"
            placeholder="ElevateMart Bot #1"
            value={form.name}
            onChange={v => setForm(f => ({ ...f, name: v }))}
            required
          />
          <Field
            label="Domain URL"
            placeholder="https://mybot.example.com"
            value={form.domain}
            onChange={v => setForm(f => ({ ...f, domain: v }))}
            required
            hint="The base URL of your bot server"
          />
          <Field
            label="Secret Token"
            placeholder="your-secret-token-here"
            value={form.token}
            onChange={v => setForm(f => ({ ...f, token: v }))}
            type="password"
            hint="Used for protected API endpoints"
          />
          <Field
            label="Note (optional)"
            placeholder="e.g. Used for withoutorders campaign"
            value={form.note}
            onChange={v => setForm(f => ({ ...f, note: v }))}
          />

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl font-display font-600 text-sm text-slate-400 transition-colors"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl font-display font-600 text-sm text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #15803d, #22c55e)' }}
            >
              {saving && (
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              )}
              {saving ? 'Saving...' : agent ? 'Update' : 'Add Agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, type = 'text', hint }) {
  return (
    <div>
      <label className="block text-xs font-display font-600 text-slate-400 uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-brand-400">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        className="w-full px-3.5 py-2.5 rounded-xl text-sm font-body text-white placeholder-slate-600 outline-none transition-all"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
        onFocus={e => e.target.style.border = '1px solid rgba(34,197,94,0.4)'}
        onBlur={e => e.target.style.border = '1px solid rgba(255,255,255,0.08)'}
      />
      {hint && <p className="text-slate-600 text-xs mt-1">{hint}</p>}
    </div>
  );
}